import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {LoadingOutlined, UploadOutlined} from '@ant-design/icons';
import { message, Spin, Upload } from 'antd';
import './Styles/UploadLogo.css';

export default function UploadLogo() {
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const navigate = useNavigate();

    const validateFile = (file) => {
        if (!file || !file.type) {
            console.error("Invalid file or file type is missing.");
            return false;
        }

        const isImage = file.type === 'image/png' || file.type === 'image/jpeg';
        const isLt2M = file.size / 1024 / 1024 < 2;

        if (!isImage) {
            message.error('Only JPG/PNG files are allowed');
            return false;
        }

        if (!isLt2M) {
            message.error('Image must be smaller than 2MB');
            return false;
        }

        return isImage && isLt2M;
    };

    const previewImage = async (file) => {
        setPreviewUrl(true);

        try {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            try {
                reader.readAsDataURL(file);
            } catch (readError) {
                console.error('Error reading file:', readError);
            }
        } catch (error) {
            console.error('Error previewing image:', error);
        }
    };

    const handleBeforeUpload = (file) => {
        const isValid = validateFile(file);
        if (isValid) {
            previewImage(file);
            setFile(file);
        }
        return false;
    };

    const handleProceed = async () => {
        if (!file) {
            message.error('Please upload your logo before proceeding');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append("logo", file);

            const res = await fetch("", {
                method: "POST",
                body: formData,
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.message || "Upload failed");
            }

            message.success("Logo uploaded successfully");
            navigate('/job-admin');
        } catch (err) {
            message.error(err.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="job-upload-container">
            <div className="job-upload-content">
                <img src="https://res.cloudinary.com/diomlp95m/image/upload/v1752735823/Youthplus_Logoo_pjfjku.jpg" alt="YouthPlus" className="job-upload-logo" />

                <h2 className="job-upload-heading">Upload Company Logo</h2>
                <p className="job-upload-subtext">
                    Kindly upload your company logo. This is what will be attached to your job posts.
                </p>

                <div className='job-upload-section'>
                    <div className='job-upload-box-back'>
                        <div className={`job-upload-box ${!previewUrl ? 'empty' : ''}`}>
                            {previewUrl ? (
                                <div className="job-uploaded-logo-preview">
                                    <img src={previewUrl} alt="Uploaded Logo" className="job-upload-preview" />
                                    <div className="job-upload-actions">
                                        <Upload
                                            showUploadList={false}
                                            beforeUpload={handleBeforeUpload}
                                            disabled={uploading}
                                        >
                                            <button className="job-replace-btn" disabled={uploading}>
                                                Replace Logo
                                            </button>
                                        </Upload>
                                        <button className="job-remove-btn" onClick={() => {
                                            setFile(null);
                                            setPreviewUrl(null);
                                        }}
                                        disabled={uploading}>
                                            Remove Logo
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <Upload
                                    showUploadList={false}
                                    beforeUpload={handleBeforeUpload}
                                    disabled={uploading}
                                >
                                    <div className="job-upload-dropzone">
                                        <UploadOutlined style={{ fontSize: 32, color: '#057C96' }} />
                                    </div>
                                </Upload>
                            )}
                        </div>
                    </div>
                    <div className='job-upload-dropzone-text'>
                        <p className="job-upload-instruction">Upload Company Logo</p>
                        <p className="job-upload-format-note">(format: png or jpeg, max size: 2mb)</p>
                    </div>
                </div>

                <button
                    onClick={handleProceed}
                    disabled={uploading}
                    className="job-upload-button"
                >
                    {uploading ? (
                        <Spin indicator={<LoadingOutlined style={{fontSize: 18, color: 'white'}} />} size="small" />
                    ) : (
                        'Proceed to Log In'
                    )}
                </button>
            </div>
        </div>
    );
}
